/* Tenant scoping context for public studio sites at /s/:slug/...

   Wraps the entire `/s/:slug` sub-tree. Exposes the current studio slug
   to every descendant — pages (`Home`, `Portfolio`, `Services`, `Booking`,
   `Contact`), components, and most importantly the `api` axios instance
   (via the request interceptor in `/app/frontend/src/lib/api.js`).

   The interceptor reads `getTenantSlug()` on every public request and
   appends `?studio=<slug>` so the backend's `resolve_public_studio_id`
   dependency knows which tenant to serve. This means the EXACT SAME
   page components used for allure-image.com also power
   yourstudio.lumetiercreatives.com — no code duplication. */

import { createContext, useContext } from "react";
import { useParams } from "react-router-dom";

const TenantContext = createContext({ slug: null });

// Module-level slug holder so the axios interceptor can read it without
// hooks — React context is not visible from inside a non-React function.
let _currentSlug = null;

export function getTenantSlug() {
  return _currentSlug;
}

export function tenantHref(path) {
  // Helper for hardcoded `<Link to="/booking">` style links inside reused
  // page components. When rendered under /s/:slug, returns the slugged
  // version. Outside the tenant tree, returns the path unchanged.
  if (!path) return path;
  if (!_currentSlug) return path;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path === "/") return `/s/${_currentSlug}`;
  // Allow callers to pass paths with or without a leading slash.
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `/s/${_currentSlug}${clean}`;
}

export function TenantProvider({ children }) {
  const { slug } = useParams();

  // CRITICAL: assign _currentSlug SYNCHRONOUSLY during render — not in a
  // useEffect. React fires child effects BEFORE parent effects, so a
  // useEffect here would lag by one render: the new tenant's children
  // (useSiteSettings, etc.) would fire their first api.get() while
  // `_currentSlug` still held the PREVIOUS tenant's value (or null), the
  // axios interceptor would omit `?studio=<slug>`, and the backend would
  // fall back to the default studio (Allure Image) — leaking Allure's
  // hero/portfolio/services into the visiting tenant's first paint.
  //
  // Also: we deliberately do NOT clear `_currentSlug` on unmount. Doing so
  // via a useEffect cleanup interleaves badly with StrictMode's
  // mount/unmount cycle in dev (cleanup ran AFTER children's NavLinks
  // were rendered, leaving them with un-prefixed hrefs like "/portfolio"
  // instead of "/s/<slug>/portfolio"). Leaving the mirror stale across
  // tenant->non-tenant navigation is harmless: the axios interceptor
  // appending `?studio=<x>` to a /api/admin/* request is ignored by the
  // backend, and the next tenant route mount will overwrite it during
  // render before any child can read it.
  // Module-level assignment during render is idempotent and safe under
  // StrictMode's double-render.
  _currentSlug = slug || null;

  return (
    <TenantContext.Provider value={{ slug: slug || null }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
